import React, { useState, useMemo } from 'react'; 
import { Button, Image, Space, Table, Tabs, Typography } from 'antd';  
import useHasError from '../../hooks/useHasError'; 
import useIsLoading from '../../hooks/useIsLoading'; 
import useAIGetPromptReport from '../../gql/hooks/useAIGetPromptReport'; 
import SpinnerCentered from '../../components/SpinnerCentered/SpinnerCentered';
import './styles.less';

const AIReportView = ( { promptId, reportId, pageSize = 5 } ) => { 
    const [ dataCount, setDataCount ] = useState( 0 );
    const [ dataShowingCount, setDataShowingCount ] = useState( 0 );
    const { promptReport, promptReportError, promptReportLoading } = useAIGetPromptReport( promptId, reportId );      
    const { isLoading } = useIsLoading( { items: [ promptReportLoading ] } );
    const { hasError } = useHasError( { items: [ promptReportError ] } );

    const tableData = useMemo( () => {
        const rows = promptReport?.rawData?.rows;
    
        if ( !rows ) {
            return undefined;
        }
 
        const result = rows.map( ( row, index ) => ( {
            ...row,
            key: index,
        } ) );

        setDataCount( promptReport.numRows );
        setDataShowingCount( result.length );
    
        return result;
    }, [ promptReport ] );

    const tableColumns = useMemo( () => {
        const headers = promptReport?.rawData?.headers;
        
        return headers?.map( header => ( { 
            title: header.replace( '_', ' ' ),
            dataIndex: header,
            className: 'report-column',
            render: ( text ) => {
                return ( <span className="ai-report-table">{ text }</span> );
            }
        } ) );
    }, [ promptReport ] );
    
    const csvURL = useMemo( () => promptReport?.csvSignedUrl || undefined, [ promptReport, promptReportLoading ] );

    const graphURL = useMemo( () => promptReport?.graphSignedUrl || undefined, [ promptReport ] );

    /*************************************
     *  ANCHOR Event Handlers
     *************************************/

    const handleDownloadCsvClick = () => { 
        window.open( csvURL, '_blank' );
    };

    const handleDownloadImageClick = () => { 
        window.open( graphURL, '_blank' );
    };
    
    /*************************************
     *  ANCHOR Render
     *************************************/
    
    const getTableHeader = () => {
        return ( 
            <div className="toolbar">
                <div className="left">
                    <Typography.Text italic>
                        { `Showing of first ${ dataShowingCount } of ${ dataCount } rows.` }
                    </Typography.Text>
                </div>

                <div className="right">
                    <Space>
                        { csvURL &&
                            <Button   
                                type="primary"
                                onClick= { () => handleDownloadCsvClick() }
                            >
                                Download .csv File  
                            </Button>
                        }
                    </Space>
                </div>
            </div>
        );
    };

    return ( 
        <div className="ai-report-view">
            { /* Loading spinner */ }
            { isLoading &&
                <SpinnerCentered/>
            }

            { /* Table showing the report */ }
            { !isLoading && !hasError &&
                <Tabs
                    type="card"
                >
                    <Tabs.TabPane 
                        tab="Report" 
                        key="report"
                    >
                        <Table
                            columns={ tableColumns }
                            dataSource={ tableData } 
                            bordered
                            title={ () => getTableHeader() }
                            scroll={ { x: 'max-content' } }
                            pagination={ { 
                                defaultPageSize: pageSize,
                                showSizeChanger: false,
                                size: 'small'
                            } }
                        />
                    </Tabs.TabPane>

                    { graphURL && 
                        <Tabs.TabPane 
                            tab="Image" 
                            key="image"
                        >
                            <div className="image-parent">
                                <div className="flex-container-columns"> 
                                    <div className="left"> 
                                        <Image
                                            width={ 500 }
                                            src={ graphURL || undefined }
                                        />
                                    </div> 
                                    <div className="right"> 
                                        <Button   
                                            type="primary"
                                            onClick= { () => handleDownloadImageClick() }
                                        >
                                            Download Image  
                                        </Button>
                                    </div> 
                                </div>
                            </div>
                        </Tabs.TabPane>
                    }
                </Tabs>
            }
        </div>
    );
};

export default AIReportView;
